import { defineStore } from 'pinia'
import moment from 'moment'

export const useSurveysStore = defineStore('surveys', {
    state: () => ({
        participant: {},
        survey: '',
        pages: [],
        response: {},
        taskSurvey3: {},

        currentIdx: 0,
        error: {},
        nextButtonDisabled: false
    }),
    actions: {
        setSurvey(surveyName) {
            this.survey = surveyName
        },
        async setParticipant(participant) {
            this.participant.id = participant.id
            this.participant.name = participant.full_name
            this.participant.ulid = participant.ulid
            if (participant.preferred_language_id == 1) this.participant.language = 'english'
            if (participant.preferred_language_id == 2) this.participant.language = 'spanish'
            if (participant.preferred_language_id == 3) this.participant.language = 'french'
            // SPECIAL CASING ALERT //
            // If these were answered in survey2, add to response, so questions can be skipped
            if (participant.delivered_at && this.survey == 'survey3') {
                this.response.PREG_STATUS = 1
                this.response.PREG_DELIVERYDATE = moment(participant.delivered_at).format('YYYY-MM-DD')
                this.saveResponse()
            }
            // SPECIAL CASING ALERT //
            // If Survey #5 button clicked to enter, need to show Welcome page and PREG_DELIVERYDATE question
            // otherwise, skip those questions
            if (this.survey == 'survey5') {
                await this.getTaskSurvey3(participant)
                if (participant.delivered_at) {
                    this.response.PREG_DELIVERYDATE = moment(participant.delivered_at).format('YYYY-MM-DD')
                    this.saveResponse()
                }
                if (this.taskSurvey3 != "" && this.taskSurvey3.task_type_status_id == 52) {
                    //
                } else {
                    this.nextPage()
                }
            }
        },
        async getTaskSurvey3(participant) {
            try {
                const result = await axios.get(`/api/task-survey3?participant_id=${participant.id}`);
                this.taskSurvey3 = result.data
            } catch (error) {
                console.error('Error loading getTaskSurvey3 response:', error);
            }
        },
        loadPages(surveyPages) {
            this.pages = JSON.parse(surveyPages)
        },
        async loadResponse(response, participant) {
            this.response = response;
            this.response.survey = this.survey
            try {
                const result = await axios.get(`/api/response?id=${this.response.id}&participant_id=${participant.id}&survey=${this.survey}`, {
                    headers: { Authorization: participant.full_name }
                });
                this.response = result.data;
                this.response.survey = this.survey;
            } catch (error) {
                console.error('Error loading response:', error);
            }
        },
        setError(errorMessage) {
            if (!errorMessage) {
                this.error = {}
                return
            }
            this.error.message = errorMessage
        },
        async nextPage() {
            if (!this.validatePage()) {
                this.setError('Please answer all questions')
                return
            }
            this.error = {}
            let currentPage = this.pages[this.currentIdx]

            if (currentPage.hasOwnProperty("afterPageHook")) {
                this.nextButtonDisabled = true;
                await this.afterPageHook()
                await this.sleep(2000)
                this.nextButtonDisabled = false;
            }

            // advance to next page
            this.currentIdx++

            // recursive b/c multiple pages in a row may be skipped
            while (this.skipPageIf()) {
                this.currentIdx++
            }
        },
        previousPage() {
            this.error = {}
            this.currentIdx--
            while (this.skipPageIf()) {
                this.previousPage()
            }
        },
        validatePage() {
            // if page skipped, no validation necessary, return true
            if (this.skipPageIf()) {
                return true 
            }

            let invalidQuestions = 0
            // loop through question objects in page file
            // by default all questions are required unless they specify: "validationRules": []
            for (let question of this.pages[this.currentIdx]['questions']) {
                if (question.hasOwnProperty("validationRules")) {
                    if (question.validationRules.length) {
                        // TODO: loop through question.validationRules and validate this.response[question.variable] against each rule
                        // if any fail, set this.setError and return false
                    } else {
                        if (question.hasOwnProperty("showQuestionIf")) {
                            this.clearHidden(question)
                        }
                        continue // to skip validation, create empty "validationRules" array in question definition file
                    }
                }

                // if question has showIf key (skip pattern), it MIGHT require a response depending on variable value
                if (question.hasOwnProperty("showQuestionIf")) {
                    if (this.showQuestionIf(question)) {
                        if (this.response[question.variable] == null) {
                            invalidQuestions++ // response null. invalidQuestion
                        }
                    }
                    // if response was changed so that hidden field is re-hidden, clear abandoned hidden value
                    this.clearHidden(question)
                } else { // non-showIf question. response required
                    if (question.template == "CheckboxDefault" || question.template == "CheckboxHeader") { // checkboxes are special case, unfortunately
                        let checkboxResponses = 0
                        for (let answer of question.answers) {
                            if (this.response[answer.variable]) {
                                checkboxResponses++
                            }
                        }
                        if (checkboxResponses < 1) {
                            invalidQuestions++
                        }
                    } else {
                        if (this.response[question.variable] == null) {
                            invalidQuestions++ // response null. invalidQuestion
                        }
                    }
                }

                // survey3 multiple babies questions (37-38) need their own validation
                if (question.template == "WeightPoundsOunces" || question.template == "RadioBabiesStatus") {
                    if (!this.validateBabyData(this.response['BIRTH_NUMBER'], this.response[question.variable])) {
                        invalidQuestions++
                    }
                    this.deleteChangedBabyData(question)
                }
            }

            return invalidQuestions ? false : true
        },
        showQuestionIf(question) {
            switch(question.showQuestionIf.rule) {
                case 'Equals':
                    return this.response[question.showQuestionIf.variable] == question.showQuestionIf.value
                    break;
                case 'ArrayIncludes':
                    const valueArray = question.showQuestionIf.value
                    return valueArray.includes(this.response[question.showQuestionIf.variable])
                    break;
                default:
                    return false  
            }
        },
        skipPageIf() {
            let page = this.pages[this.currentIdx]
            if (page.hasOwnProperty("skipPageIf")) {
                switch(page.skipPageIf.rule) {
                    case 'NotNull':
                        return this.response[page.skipPageIf.variable] !== null
                    case 'Equals':
                        skipPage = this.response[page.skipPageIf.variable] == page.skipPageIf.value
                        if (skipPage && ['LEAVE_TYPE_NONE', 'LEAVE_TYPE_PARTNER_NONE'].includes(page.skipPageIf.variable)){
                            this.clearSkipped(page)
                        }
                        return skipPage
                    case 'ArrayIncludes':
                        const valueArray = page.skipPageIf.value
                        return valueArray.includes(this.response[page.skipPageIf.variable])
                    case 'Ideation':
                        if (this.response['EPDS_HARM'] === 0)
                            return true
                        if (this.response['CSSRS_WISHDEAD'] == 1 && this.response['CSSRS_THOUGHTS'] == 1)
                            return true
                        break;
                    case 'BirthDelivery':
                        return !this.response['BIRTH_DELIVERY_VAGINAL'] && !this.response['BIRTH_DELIVERY_NORESPONSE']
                    case 'SuicidalityER':
                        if (this.response['CSSRS_IDEATION'] == 2 || this.response['CSSRS_IDEATION_INTENT'] == 2 || this.response['CSSRS_IDEATION_PLAN'] == 2) {
                            return false
                        }
                        return true
                    case 'SuicidalityNonER':
                        if (this.response['CSSRS_WISHDEAD'] == 1 && this.response['CSSRS_THOUGHTS'] == 1) {
                            return false
                        }
                        if (this.response['CSSRS_IDEATION'] == 1 && this.response['CSSRS_IDEATION_INTENT'] == 1 && this.response['CSSRS_IDEATION_PLAN'] == 1) {
                            return false
                        }
                        return true
                    // very tricky, skip rule must apply to all babies
                    case 'BabiesArrayIncludes':
                        const responseField = this.response[page.skipPageIf.variable]
                        let babiesArray = this.decodeJsonData(this.response[page.skipPageIf.variable])

                        // this is used by BABY_NICU_LENGTH to handle the case where BABY_NICU question is skipped entirely
                        if (page.skipPageIf.variable == 'BABY_NICU' && responseField == null) return true

                        let skipPage = false
                        if (page.skipPageIf.value == "allOnes") skipPage = Object.values(babiesArray).every(value => value === 1)
                        if (page.skipPageIf.value == "allTwos") skipPage = Object.values(babiesArray).every(value => value === 2)

                        // if this page is skipped, but has values from changed answer, clear them out
                        if (skipPage) {
                            this.clearSkipped(page)
                        }
                        
                        return skipPage
                    case 'NotLiving':
                        if (this.survey == 'survey2') {
                            return false
                        }

                        let babiesResponses = this.decodeJsonData(this.response[page.skipPageIf.variable])
                        const allBabiesTwoThree = Object.keys(babiesResponses).length > 0 && Object.values(babiesResponses).every(value => value === 2 || value === 3)
                        if (allBabiesTwoThree) this.clearSkipped(page)

                        // this is to handle skip patterns for FEEDING_INTENT_POST: answer (3) and FEEDINC_SUCCESS: answer(1)
                        if (['survey4', 'survey5'].includes(this.survey)) {
                            const question = page.questions[0]
                            if (['FEEDING_SUCCESS','FEEDING_SUPPORT','BSES_ENOUGHMILK','BSES_COPING','BSES_FORMULA','BSES_LATCH',
                                'BSES_MANAGE','BSES_BABYCRYING','BSES_INTENTION','BSES_FAMILY','BSES_SATISFACTION','BSES_TIMECONSUMING',
                                'BSES_SWITCH','BSES_BFEVERYFEEDING','BSES_BABYDEMANDS','BSES_BABYFINISHED'].includes(question.variable) 
                                && (this.response['FEEDING_INTENT_POST'] === 3 || this.response['FEEDING_SUCCESS'] === 1)) {
                                return true
                            }
                        }

                        return allBabiesTwoThree
                    default:
                        return false
                }
            }
            return false
        },
        async afterPageHook() {
            let page = this.pages[this.currentIdx]
            let total = 0
            // ALL SPECIAL CASES. No general afterPageHook mechanism //
            switch(page.afterPageHook.rule) {
                case 'PREG_STATUS':
                    if (this.survey == 'survey3' && this.response['PREG_STATUS'] == 2)
                        window.location.href = '/non-final-survey-exit?surveyName=survey3&ulid='+ this.participant.ulid
                    break
                case 'PREG_DELIVERYDATE':
                    // NOTE: delivery date needs to be saved to participant record, as soon as known. 
                    // Handling in boot method of survey2/survey3 response models
                    if (this.survey == 'survey2' && this.response['PREG_DELIVERYDATE']) {
                        await this.finalizeSurvey()
                        break
                    }
                    // can't take survey3 until 2 weeks after delivery
                    if (this.survey == 'survey3' && this.response['PREG_DELIVERYDATE'] && moment().subtract(14, 'days').isBefore(this.response['PREG_DELIVERYDATE'])) {
                        window.location.href = '/non-final-survey-exit?surveyName=survey3&ulid='+ this.participant.ulid
                        break
                    }
                    // if 12+ weeks have passed since delivery, need survey 5
                    if (this.survey == 'survey3' && moment(this.response['PREG_DELIVERYDATE']).add(84, 'days').isSameOrBefore(moment())) {
                        await this.finalizeSurvey()
                        break
                    }
                    break
                case 'FEEDING_INTENT_PREG':
                    if (this.response['FEEDING_INTENT_PREG'] == 1)
                        this.finalizeSurvey()
                    break
                case 'EPDS_HARM':
                    const fields = ["EPDS_LAUGH","EPDS_ENJOY","EPDS_BLAME","EPDS_ANXIOUS","EPDS_SCARED","EPDS_OVERWHELMED","EPDS_SLEEP","EPDS_SAD","EPDS_CRY","EPDS_HARM"]
                    let total = 0
                    for (const field of fields) {
                        const value = Number(this.response[field]) || 0
                        total += value
                    }
                    if (total >= 14 && total < 31) {
                        axios.post('/api/survey-alert', { 
                            participant_id: this.participant.id, 
                            survey: this.survey, 
                            type: 'Depression',
                            score: total
                        }, {
                            headers: { Authorization: this.participant.name }
                        })
                        .catch(error => { 
                            console.log(error)
                            this.sendSlackNotification(`Error sending survey alert: ${error.message}\nSurvey: ${this.survey}\nParticipant: ${this.participant.id}\nScore: ${total}`)
                        })
                    }
                    if (total > 30 || total < 0) {
                        this.sendSlackNotification(`EPDS Abnormal Score Alert:\nScore: ${total}\nSurvey: ${this.survey}\nParticipant: ${this.participant.id}`)
                    }
                    break
                case 'CSSRS_WISHDEAD':
                case 'CSSRS_THOUGHTS':
                    if (this.response['CSSRS_WISHDEAD'] == 1 && this.response['CSSRS_THOUGHTS'] == 1) {
                        axios.post('/api/survey-alert', { 
                            participant_id: this.participant.id, 
                            survey: this.survey, 
                            type: 'Suicidality Non-ER' 
                        }, {
                            headers: { Authorization: this.participant.name }
                        })
                        .catch(error => { console.log(error) })
                    }
                    break
                case 'CSSRS_IDEATION':
                case 'CSSRS_IDEATION_INTENT':
                case 'CSSRS_IDEATION_PLAN':
                    if (this.response['CSSRS_IDEATION'] == 1 && this.response['CSSRS_IDEATION_INTENT'] == 1 && this.response['CSSRS_IDEATION_PLAN'] == 1) {
                        axios.post('/api/survey-alert', { 
                            participant_id: this.participant.id, 
                            survey: this.survey, 
                            type: 'Suicidality Non-ER' 
                        }, {
                            headers: { Authorization: this.participant.name }
                        })
                        .catch(error => { console.log(error) })
                    }
                    if (this.response['CSSRS_IDEATION'] == 2 || this.response['CSSRS_IDEATION_INTENT'] == 2 || this.response['CSSRS_IDEATION_PLAN'] == 2) {
                        axios.post('/api/survey-alert', { 
                            participant_id: this.participant.id, 
                            survey: this.survey, 
                            type: 'Suicidality ER' 
                        }, {
                            headers: { Authorization: this.participant.name }
                        })
                        .catch(error => { console.log(error) })
                    }
                    break
                case 'FEEDING':
                    if (this.survey == 'survey3' && this.response[page.afterPageHook.variable] == page.afterPageHook.value) {
                        this.finalizeSurvey()
                    }
                    break
                case 'BSS_CLEANROOM':
                    let babiesResponses = this.decodeJsonData(this.response['BABY_STATUS'])

                    const allBabiesTwoThree = Object.keys(babiesResponses).length > 0 && Object.values(babiesResponses).every(value => value === 2 || value === 3)
                    
                    if (this.survey == 'survey3' && allBabiesTwoThree) {
                        this.finalizeSurvey()
                    }
                    break
                case 'PP_VISIT_BC_DISCUSS':
                    if (this.response['PP_VISIT_BC_DISCUSS'] == 1 || this.response['PP_VISIT_BC_DISCUSS'] == 99)
                        this.finalizeSurvey()
                    break
                case 'POSTPARTUM_CHECKUP':
                    if (this.response['POSTPARTUM_CHECKUP'] == 1)
                        this.finalizeSurvey()
                    break
                default:
                    return false
            }
        },
        validateBabyData(numBabies, babyData) {
            if (babyData === null) return false

            let jsonBabies = this.decodeJsonData(babyData)

            for (let i = 1; i <= numBabies; i++) {
                const key = 'baby'+ i
                if (!jsonBabies[key]) {
                    return false
                }
            }

            return true
        },
        decodeJsonData(jsonData) {
            let jsonResponses

            if (jsonData === null) {
                jsonResponses = null
            } else if (typeof jsonData === 'string') {
                try {
                    jsonResponses = JSON.parse(jsonData)
                } catch (e) {
                    console.error('Error parsing JSON:', e)
                    return false
                }
            } else if (typeof jsonData === 'object' && jsonData !== null) {
                jsonResponses = jsonData
            } else {
                console.error('Unexpected type for jsonData:', jsonData);
                return false
            }

            return jsonResponses
        },
        clearHidden(question) {
            if (!this.showQuestionIf(question) && this.response[question.variable] != null) {
                this.response[question.variable] = null
                this.saveResponse()
            }
        },
        clearSkipped(page) {
            for (const question of page.questions) {
                if (this.response[question.variable] !== null) {
                    this.response[question.variable] = null
                    this.saveResponse()
                }
            }
        },
        deleteChangedBabyData(question) {
            const numBabies = this.response['BIRTH_NUMBER']
            const babyData = this.response[question.variable]
            let jsonBabies = this.decodeJsonData(babyData)

            if (jsonBabies === null || numBabies == Object.keys(jsonBabies).length) {
                return
            }

            for (let key in jsonBabies) {
                if (key.match(/\d+$/)[0] > numBabies) {
                    delete jsonBabies[key]
                }     
            }

            this.saveResponseBabies(question.variable, jsonBabies)
        },
        sendSlackNotification(message) {
            axios.post('/api/slack-notification', { message })
                .then(() => console.log('Slack notification sent successfully'))
                .catch(error => console.error('Error sending Slack notification:', error.message))
        },
        async saveResponse() {
            return axios.post('/api/response', this.response, {
                headers: { Authorization: this.participant.name }
            })
            .then(response => {
                this.response = response.data
                this.response.survey = this.survey
            })
            .catch(error => { 
                console.log(error) 
            })
        },
        saveResponseBabies(questionVariable, jsonResponse) {
            this.response[questionVariable] = jsonResponse
            this.saveResponse()
        },
        async finalizeSurvey() {
            if (!this.validatePage()) {
                this.setError('Please answer all questions')
                return
            }
            this.error = {}
            await this.saveResponse()
            window.location.href = '/finalize-survey?survey='+ this.survey +'&ulid='+ this.response.id
        },
        async sleep(ms) {
          return new Promise(resolve => setTimeout(resolve, ms));
       }
    },
})